// API base URL — 跨 web (Vite proxy) / Capacitor (file://) 兼容
//
// 三种运行环境：
//
// 1) 浏览器开发：vite dev → "/api/*" 走 vite proxy 转 :3000
// 2) 生产 web 部署：浏览器访问 https://ulucu.eheluo.com → "/api/*" 同源
// 3) Capacitor 包壳：webview 加载 file:// 或 capacitor://localhost
//    /api 是相对路径 → 命中 webview 自身（404）
//    必须用绝对 URL 指向真实后端（VITE_API_BASE 注入）
//
// 用法：
//   import { apiUrl } from './utils/api-base';
//   apiUrl('/api/stream?url=...')   // → 'https://ulucu.eheluo.com/api/stream?url=...' 在 mobile mode
//   apiUrl('/api/stream?url=...')   // → '/api/stream?url=...' 在普通 web mode

// Capacitor 兜底：相对 /api 在 file:// webview 里命中自身 → 404，所有调用全死。
// 正确姿势是 `pnpm sync:android`（vite --mode mobile 加载 .env.mobile）。
// 这里加兜底是为了配错 build 命令时 APK 仍能跑（控制台 warn 提醒修）。
const PROD_FALLBACK = 'https://ulucu.eheluo.com';
function detectBase(): string {
	const fromEnv = import.meta.env.VITE_API_BASE?.replace(/\/$/, '');
	if (fromEnv) return fromEnv;
	if (typeof window !== 'undefined') {
		// 复用下方 isCapacitor 的检测（兼容 Capacitor 6+/老版）
		const w = window as unknown as { Capacitor?: { isNative?: boolean; isNativePlatform?: () => boolean; getPlatform?: () => string } };
		const cap = w.Capacitor;
		const inCap = !!cap && (
			(typeof cap.isNativePlatform === 'function' && cap.isNativePlatform())
			|| (typeof cap.getPlatform === 'function' && cap.getPlatform() !== 'web')
			|| !!cap.isNative
		);
		if (inCap) {
			console.warn('[api-base] VITE_API_BASE missing in Capacitor build — falling back to', PROD_FALLBACK, '. Use `pnpm sync:android` (--mode mobile) to inject .env.mobile.');
			return PROD_FALLBACK;
		}
	}
	return '';
}
const RAW_BASE = detectBase();

/** axios baseURL —— 不含 /api 前缀，让调用方写完整路径 */
export const API_ORIGIN = RAW_BASE;

/** axios baseURL =  RAW_BASE + '/api' */
export const API_BASE_URL = `${RAW_BASE}/api`;

/** 拼绝对 URL：path 必须以 / 开头（如 '/api/stream?url=...' 或 '/play/index.m3u8'）*/
export function apiUrl(path: string): string {
	if (!path.startsWith('/')) path = `/${path}`;
	return `${RAW_BASE}${path}`;
}

/** 是否运行在 Capacitor 包壳里（mobile app）。Capacitor 6+ 用 isNativePlatform()，老版本 isNative 字段 */
export function isCapacitor(): boolean {
	if (typeof window === 'undefined') return false;
	const w = window as unknown as { Capacitor?: { isNative?: boolean; isNativePlatform?: () => boolean; getPlatform?: () => string } };
	const cap = w.Capacitor;
	if (!cap) return false;
	if (typeof cap.isNativePlatform === 'function') return cap.isNativePlatform();
	if (typeof cap.getPlatform === 'function') return cap.getPlatform() !== 'web';
	return Boolean(cap.isNative);
}
